import { MAX_NUMBER_OF_CHARACTERS_IN_CARD } from 'constants';
import AbstractModelType from '../AbstractModelType';
import {
  checkIsMobileApp,
  generateUUID,
  getPKFromID,
  truncateSnippet,
  locationLogic,
} from 'utilities';
import {
  getIDBObjects,
  getMobilePseudoIDBObjects,
} from 'utilities/idbUtilities';

// Shared logic for all user content items.
class UserContentItem extends AbstractModelType {
  /**
   * A name to use for IndexedDB object stores
   */
  static typeName;

  /**
   * Not all user content items have a book location, but they should all at least have a book.
   */
  book;

  /**
   * Give the item a UUID in case it is not yet saved to the backend.
   */
  uuid;

  /**
   * An object to track relations between UUIDs (generated for new user content items)
   * and IDs from the backend (graphql ids)
   */
  static uuidIdMap = {};

  /**
   * An object to hold promises that indicate that the item with a given UUID is being
   * persisted to the backend.
   */
  static uuidsPersisting = {};

  /**
   * Three mutually-exclusive state properties for user content items.
   *
   * Calling save() will make the instance "saved."
   * When the app is online, a "saved" instance will push changes to the backend to make itself persistedOnce,
   * and it will continue to push changes to keep itself persisted.
   */
  isSaved; // written to the local database, and intended to be written to the backend
  isPersistedOnce; // written to the backend database at least once
  isPersisted; // latest updates written to the backend database

  /**
   * Track whether the item should be deleted.
   */
  shouldDelete;

  /**
   * User ID for the user who created this user content item.
   */
  #userId;

  /**
   * To tie IndexedDB updates to React,
   * as there is no way of directly observing changes to IndexedDB.
   */
  static onIndexedDBUpdate;

  /**
   * State to prevent multiple asynchronous persistence tasks from running at once and clobbering each other.
   */
  static isPersistingLocalItems;

  /**
   * This is for versions of the mobile app that do not have reader-based content sync,
   * which includes older version of the React Native app as well as the Xamarin app.
   * Fake Indexed DB: to bypass adding to indexed DB and the whole process if indexedDB is not available
   * fakeStoreDB: Object to act as store of IDB to keep the saved UserContent in memory which will be persisted.
   * fakeTransaction: always resolves to true. to mimic adding to idb store
   * fakeStore: mimic idb Store, has put, delete and openCursor methods mimicing that of idb. 'put' adds data, 'delete' deletes data and 'openCursor' iterates through everything in the store.
   */
  static fakeIDB = {
    fakeStoreDB: {
      graphql: {},
      book: {},
      highlights: {},
      notes: {},
      bookmarks: {},
      copiedTexts: {},
      flashcards: {},
      flashcardDecks: {},
      offlineSessions: {},
    },
    fakeTransaction: { complete: Promise.resolve() },
    fakeStore: (storeName) => {
      const store = this.fakeIDB.fakeStoreDB[storeName];

      if (!store) {
        throw new Error('Invalid storeName specified.');
      }

      return {
        openCursor: () => {
          const dataKeys = Object.keys(store);
          let count = 0;

          const cursorBuilder = (count) => {
            return {
              key: dataKeys[count],
              value: store[dataKeys[count]],
              continue: () => {
                count++;
                if (count < dataKeys.length) {
                  return cursorBuilder(count);
                } else {
                  return null;
                }
              },
            };
          };

          if (!dataKeys.length) {
            return null;
          }

          return cursorBuilder(count);
        },
        put: (value, key) => {
          return new Promise((resolve) => {
            store[key] = value;
            resolve();
          });
        },
        delete: (key) => {
          return new Promise((resolve) => {
            delete store[key];
            resolve();
          });
        },
      };
    },
  };

  constructor(properties = {}) {
    super(...arguments);

    if (this.constructor === UserContentItem) {
      throw new Error('This class is abstract. It cannot be instantiated.');
    }

    this.book =
      properties.document?.book ??
      properties.page?.book ??
      properties.marker?.book ??
      properties.book;

    if (!this.book) {
      throw new Error(
        'A book is required when instantiating a user content item.',
      );
    }

    this.uuid =
      properties.uuid ??
      Object.keys(
        (this.constructor.uuidIdMap[this.constructor.typeName] ??= {}),
      ).find(
        (uuid) =>
          this.constructor.uuidIdMap[this.constructor.typeName][uuid] ===
          this.id,
      ) ??
      generateUUID();

    this.dateCreated = properties.dateCreated || new Date().toISOString();

    this.#userId = properties.userId;

    // If the instance has an id, it was persisted to the backend database at least once.
    this.isPersistedOnce = Boolean(this.id);

    // Instances that have been persisted to the backend at least once are "saved" there.
    // For instances "saved" to IndexedDB, we can rely on receiving a fromLocalDB property.
    this.isSaved = this.isPersistedOnce || properties.fromLocalDB;

    // If the instance has an id, and didn't come from the local DB, it is the latest version of the record from the backend.
    this.isPersisted = this.isPersistedOnce && !properties.fromLocalDB;

    this.shouldDelete = properties.shouldDelete ?? false;
  }

  /**
   * Allows the id to immediately become available as soon as the item is persisted.
   */

  get superId() {
    return super.id;
  }

  get id() {
    return (
      this.superId ??
      this.constructor.uuidIdMap[this.constructor.typeName]?.[this.uuid]
    );
  }

  get pk() {
    return this.id ? getPKFromID(this.id) : null;
  }

  get key() {
    return this.id ?? this.uuid;
  }

  get userId() {
    return this.#userId;
  }

  // Provides some common IndexedDB objects.
  static async getIDBObjects(bookPk, write = false) {
    const idbObjects = await getIDBObjects(bookPk, this.typeName, write);

    if (
      !idbObjects ||
      checkIsMobileApp(
        locationLogic.getSearchStringFromLocation(window.location),
      )
    ) {
      // If the window.RedshelfWebView object is available,
      // use the onmessage-based pseudo-IDB adapter.
      // This is for the new reader-based sync feature in the React Native mobile app.
      // We must also check the query param because some versions of the old mobile app
      // will have the window object but not the new sync feature.
      if (
        window.RedshelfWebView &&
        new URLSearchParams(
          locationLogic.getSearchStringFromLocation(window.location),
        ).has('mobileAppWithNewSync')
      ) {
        return await getMobilePseudoIDBObjects(bookPk, this.typeName);
      }

      // If the window.RedshelfWebView object is not available,
      // but we were not able to get IndexedDB objects,
      // use fake IDB objects so that the app doesn't break.
      // This is for Xamarin and older React Native.
      const { fakeTransaction, fakeStoreDB } = this.fakeIDB;
      const store = this.fakeIDB.fakeStore(this.typeName);

      return {
        db: fakeStoreDB,
        transaction: fakeTransaction,
        store: store,
      };
    }

    return idbObjects;
  }

  /**
   * Commits the instance to be saved to the backend by first saving it to IndexedDB.
   *
   * If it has an ID of an existing record, it will overwrite the record with current properties.
   * If it does not, it will create a new record.
   */
  async save() {
    // Wait until it does not have an request in flight before saving updates to the local db.
    await this.constructor.uuidsPersisting[this.uuid];

    const { transaction, store } = await this.constructor.getIDBObjects(
      this.book.pk,
      true,
    );

    try {
      const serializedObject = this.serialize();

      await store.put(serializedObject, this.id ?? this.uuid);

      await transaction.complete;
      this.constructor.onIndexedDBUpdate?.();
    } catch (error) {
      console.log(
        `Error saving ${this.constructor.typeName} with ${
          this.id ? 'ID' : 'UUID'
        } ${this.id ?? this.uuid} to IndexedDB`,
        error.message,
      );
    }

    return this;
  }

  /**
   * Commits the instance to be deleted from the backend by first marking it as deleted in IndexedDB.
   */
  async delete() {
    // Wait in case the item is currently being persisted.
    await this.constructor.uuidsPersisting[this.uuid];

    if (this.isPersistedOnce) {
      // If this model is persisted to the backend,
      // save to the local database with shouldDelete: true to signal deletion
      await this.change({ shouldDelete: true }).save();

      return null;
    }

    // If the model was never persisted to the backend, just delete the entry from the local database.
    const { transaction, store } = await this.constructor.getIDBObjects(
      this.book.pk,
      true,
    );

    try {
      await store.delete(this.uuid);

      await transaction.complete;
      this.constructor.onIndexedDBUpdate?.();
    } catch (error) {
      console.log(
        `Error deleting ${this.constructor.typeName} with UUID ${this.uuid} from IndexedDB`,
        error.message,
      );
    }

    return null;
  }

  /**
   * Retrieves a list of new instances of the user content type from the local database.
   *
   * @returns {Promise<Array>} an array of instances
   */
  static async getLocalItems(book) {
    const { store } = await this.getIDBObjects(book.pk);

    let cursor = await store.openCursor();
    const instances = [];

    while (cursor) {
      const instance = this.deserialize(book, {
        ...cursor.value,
        fromLocalDB: true,
      });

      instances.push(instance);

      cursor = await cursor.continue();
    }

    return instances;
  }

  /**
   * Takes all items in the local database and saves them to or deletes them from the backend
   *
   * This should be run whenever the app loads or goes from an offline to an online state.
   */
  static async persistLocalItems(book, mutations, refetch = () => {}) {
    if (this.isPersistingLocalItems) {
      return 0;
    }

    this.isPersistingLocalItems = true;

    const itemCount = await this.persistLocalItemsRecursive(
      book,
      mutations,
      refetch,
    );

    if (itemCount) {
      // Only trigger the update callback if items were modified.
      this.onIndexedDBUpdate?.();
    }

    this.isPersistingLocalItems = false;

    return itemCount;
  }

  // Helper function that does the bulk of the work for persistLocalItems
  static async persistLocalItemsRecursive(book, mutations, refetch) {
    let indexedDBChangesCount = 0;
    const { store } = await this.getIDBObjects(book.pk);
    let cursor = await store.openCursor();
    const instances = new Map();

    // Iterate through IndexedDB records
    while (cursor) {
      // Ignore items that are already persisting
      if (!this.uuidsPersisting[cursor.value.uuid]) {
        const instance = this.deserialize(book, cursor.value);

        // Collect the instance in the temporary map
        instances.set(cursor.key, instance);
      }

      // Onto the next one
      cursor = await cursor.continue();
    }

    // Collect all of the instances before persisting.
    // IndexedDB will close the connection if we try to do something else unrelated that is also asynchronous.
    for (const [key, instance] of instances) {
      let resolve;

      // Defer subsequent saves until the latest data is saved to the backend.
      this.uuidsPersisting[instance.uuid] = new Promise((r) => {
        resolve = r;
      });

      try {
        // Call the instance's type-specific persist function.
        const newId = await instance.persist(mutations);

        // Map an ID to the instance UUID if an ID was returned.
        if (newId) {
          const uuidIdMapForType = (this.uuidIdMap[
            instance.constructor.typeName
          ] ??= {});
          uuidIdMapForType[instance.uuid] = newId;
        }

        // Delete the item from the local database
        // Do this for each instance at a time in case something blows up and it only pushes up some of them.
        const { store } = await this.getIDBObjects(book.pk, true);
        await store.delete(key);

        indexedDBChangesCount += 1;
      } catch (e) {
        console.warn(
          'Failed to persist user content item to the backend.',
          e,
          instance,
        );
        // If we get permission denied errors, we remove from local storage so that it doesn't keep trying to persist to backend.
        // Refetching will make sure we have the latest data, in case out-of-sync data is causing permission errors.
        // Offline sessions exceeding the DRM limit could be created if user dont refresh the page, and sessions have been created in other devices, resulting in 403 from backend.
        if (e?.graphQLErrors?.[0]?.status === 403) {
          delete this.uuidsPersisting[instance.uuid];

          const { store } = await this.getIDBObjects(book.pk, true);
          await store.delete(key);

          indexedDBChangesCount += 1;

          await refetch();
        }
      }

      // Important that this is resolved after the existing item is removed from the local database
      // so that subsequent changes to the local database from calls to save or delete are not blown away.
      // See the `await this.constructor.uuidsPersisting[instance.uuid]` in `save()`
      resolve();
      delete this.uuidsPersisting[instance.uuid];
    }

    // We cancel any attempt to run this function while it is already running,
    // so there may be updates in the local database that were added while data was persisting.
    // This recursive call makes sure that every time this method runs, it runs until there are no items.
    if (indexedDBChangesCount > 0) {
      indexedDBChangesCount += await this.persistLocalItemsRecursive(
        book,
        mutations,
        refetch,
      );
    }

    return indexedDBChangesCount;
  }

  /**
   * Using each configuration of uuidIdRelations for the type,
   * go through IndexedDB and replace uuids with ids.
   *
   * This is used to replace all flashcard deckKey values with available flashcard deck ids.
   */
  static async replaceLocalUuids(book, typeName) {
    const { store } = await this.getIDBObjects(book.pk, true);
    let cursor = await store.openCursor();

    while (cursor) {
      for (let { type, key } of this.uuidIdRelations) {
        const id = type.uuidIdMap[typeName][cursor.value[key]];

        if (id) {
          cursor.value[key] = id;
          await store.put(cursor.value, cursor.key);
        }
      }

      cursor = await cursor.continue();
    }
  }

  /**
   * The app is not truly object-oriented, and the user content item objects cannot be changed,
   * so this method provides a convenient way to generate a new instance of the user content item
   * with given properties changed.
   *
   * @param {Object} properties - an object mapping property names to values
   * @returns {UserContentItem} a new instance of the user content item, with changed properties
   */
  change(properties) {
    for (let property of ['id', 'uuid']) {
      if (property in properties) {
        throw new Error(
          `You cannot change the ${property} of a user content item.`,
        );
      }
    }

    return this.constructor.deserialize(this.book, {
      ...this.serialize(),
      ...properties,
    });
  }

  /**
   * Deserializes a POJO and returns a new instance of the user content item.
   *
   * @param {Book} book
   * @param {Object} serializedObject
   * @returns {UserContentItem}
   */
  static deserialize(book, serializedObject) {
    return new this({ book, ...serializedObject });
  }

  /**
   * Serializes the model into a POJO to store in indexeddb
   *
   * Extend this method in subclasses, and include `...super.serialize()`, to add properties.
   *
   * @returns {Object}
   */
  serialize() {
    const { id, pk, uuid, userId, dateCreated, shouldDelete } = this;

    return {
      id,
      pk,
      uuid,
      userId,
      dateCreated,
      shouldDelete,
    };
  }

  /**
   * On the backend, create, update, or delete the user content item.
   *
   * Generally:
   *   This method should run a delete mutation when shouldDelete is true (if it should allow deleting),
   *   an update mutation when a pk/id exists on the instance (if it should allow updating)
   *   and a create mutation otherwise.
   *
   * It should return the record id when creating or updating.
   */
  async persist(mutations) {
    console.warn(
      `persist() is not implemented for ${this.constructor.typeName}.`,
    );
  }

  /**
   * An object to track truncatedSnippet to the actual snippet
   */
  static snippetCache = {};

  /**
   * Truncates snippet to fit 5 lines and width of 217px LT menu
   * @param {string} snippet snippet of text selected for highlight, note or copiedText
   * @returns truncated version of snippet to fit in given height and width
   */
  static getTruncatedSnippet(snippet) {
    if (!snippet || snippet.length < MAX_NUMBER_OF_CHARACTERS_IN_CARD) {
      return snippet;
    }

    if (this.snippetCache[snippet]) {
      return this.snippetCache[snippet];
    }

    this.snippetCache[snippet] = truncateSnippet(snippet);

    return this.snippetCache[snippet];
  }
}

export default UserContentItem;
